/**
 * A component to handle the browse-and-select mechanics for file uploads.
 */

import { useRef } from "react";
import { Button } from "react-bootstrap";
import { joinClasses } from "@/utils/styling";

interface Props {
  onSelect: (files: File[]) => void;
  variant?: string; // Bootstrap variant for the button (e.g., 'primary', 'secondary', etc.)
  fileTypes?: string[]; // Array of accepted MIME types (e.g., ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'])
  acceptExtensions?: string[]; // Array of accepted file extensions (e.g., ['.csv']). More reliable than MIME types across browsers.
  multiple?: boolean; // Allow multiple file selection
  className?: string;
  children?: React.ReactNode;
}

export default function BrowseFilesButton({
  onSelect,
  variant = "outline-primary",
  fileTypes,
  acceptExtensions,
  multiple = false,
  className,
  children = "Browse Files",
}: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleClick = async () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files && files.length > 0) {
      const selectedFiles = Array.from(files);
      onSelect(selectedFiles);
    }
    input.value = "";
  };

  // The accept attribute takes a comma-separated mix of MIME types and file
  // extensions. We include extensions (e.g. ".csv") because some browsers
  // (notably Firefox) filter the file picker by MIME type inconsistently, so a
  // MIME-only accept list can hide valid .csv files. Extensions are reliable.
  const acceptValue =
    [...(fileTypes ?? []), ...(acceptExtensions ?? [])].join(", ") || undefined;

  return (
    <>
      <Button
        variant={variant}
        type="button"
        className={joinClasses("browse-files-btn", className)}
        onClick={handleClick}
      >
        {children}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        aria-label="Choose CSV files to upload"
        tabIndex={-1}
        style={{ display: "none" }}
        multiple={multiple}
        accept={acceptValue}
        onChange={handleFileSelect}
      />
    </>
  );
}